import { Card, CardContent } from '@/components/ui/card'
import { DeltaBadge } from '@/features/reports/components/delta-badge'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

interface KpiTileProps {
  label: string
  value: string
  current: number
  previous: number | null | undefined
  againstLabel?: string
  /** Replaces the delta — used by the forecast tile, whose figure has nothing to compare against. */
  footer?: React.ReactNode
  /**
   * `hero` inverts the tile. Reserved for the one figure the page is about: on a wall of four
   * equally weighted cards the reader has to decide what to look at first, and revenue is always
   * the answer. More than one inverted tile would take the ranking back out.
   */
  variant?: 'default' | 'hero'
  /**
   * A second comparison of the same figure, under the first.
   *
   * A single day is the one period with two questions worth asking — "besser als gestern" and
   * "besser als letzten Freitag" — and they answer differently often enough to matter: a strong
   * Friday against a weak Thursday says nothing about the trade. Only rendered from `sm:` up,
   * because {@link DeltaBadge} drops its "vs." label on a phone and two bare percentages under
   * one figure say nothing at all.
   */
  secondary?: { previous: number | null | undefined; againstLabel: string }
  /** A second line under the delta — the comparison window, for instance. */
  caption?: string
  loading?: boolean
}

/**
 * One headline figure (CHG-2026-09-manager-reporting).
 *
 * The value uses the font's default proportional figures, not `tabular-nums`: equal-width digits
 * make a number like 121 look loose at display sizes, and these four tiles sit side by side, so
 * nothing needs to align vertically. Tabular figures are for columns.
 */
export function KpiTile({
  label,
  value,
  current,
  previous,
  againstLabel,
  footer,
  variant = 'default',
  secondary,
  caption,
  loading,
}: KpiTileProps) {
  const hero = variant === 'hero'
  return (
    <Card
      className={cn(
        hero && 'border-transparent bg-foreground text-background shadow-sm',
      )}
    >
      <CardContent
        className={cn(
          'flex flex-col gap-1.5 px-3 sm:px-6',
          hero && 'justify-center',
        )}
      >
        <div
          className={cn(
            'text-xs sm:text-sm',
            hero ? 'text-background/70' : 'text-muted-foreground',
          )}
        >
          {label}
        </div>
        {loading ? (
          <Skeleton
            className={cn('h-9 w-28', hero && 'bg-background/20')}
            aria-hidden="true"
          />
        ) : (
          // Two tiles per row on a phone: € 26.183,90 at text-3xl overflows a 375px column, so the
          // display size steps up with the viewport rather than wrapping mid-figure.
          <div
            className={cn(
              'font-semibold tracking-tight',
              hero
                ? 'text-3xl leading-9 sm:text-4xl sm:leading-11'
                : 'text-lg leading-6 sm:text-3xl sm:leading-9',
            )}
          >
            {value}
          </div>
        )}
        {loading ? (
          <Skeleton
            className={cn('h-4 w-24', hero && 'bg-background/20')}
            aria-hidden="true"
          />
        ) : (
          (footer ?? (
            <>
              <DeltaBadge
                current={current}
                previous={previous}
                againstLabel={againstLabel}
                tone={hero ? 'inverse' : 'default'}
              />
              {secondary ? (
                <DeltaBadge
                  className="hidden sm:flex"
                  current={current}
                  previous={secondary.previous}
                  againstLabel={secondary.againstLabel}
                  tone={hero ? 'inverse' : 'default'}
                />
              ) : null}
            </>
          ))
        )}
        {caption && !loading ? (
          <div
            className={cn(
              'text-xs tabular-nums',
              hero ? 'text-background/60' : 'text-muted-foreground',
            )}
          >
            {caption}
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}
